import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import { ScaleIcon, BookOpenIcon, PuzzlePieceIcon, PlusCircleIcon, ChevronRightIcon, ClockIcon, DocumentCheckIcon, Bars3Icon, MegaphoneIcon, HomeIcon } from '@heroicons/react/24/outline'
import { RYNEK_WLACZONY } from '../config/rynek'
import { useSelection } from '../context/SelectionContext'
import { useAuth } from '../context/AuthContext'
import { PLAKIETKA_CTA, PROMOCJA_AKTYWNA } from '../config/promocjaPartnerska'
import { useTranslation } from 'react-i18next'
import { jestPolska } from '../kraj'
import { MENU_NARZEDZI, PASEK_NARZEDZI } from '../config/nawigacja'
import MenuAsystentow from './MenuAsystentow'
import SzufladaNarzedzi from './nawigacja/SzufladaNarzedzi'
import MenuNarzedzi from './nawigacja/MenuNarzedzi'
import PlakietkaRynku, { etykietaZLicznikiem, useLicznikRynku } from './nawigacja/LicznikRynku'
import KategoriaSzukania, { dostepneKategorie, kategoriaZeSciezki } from './nawigacja/KategoriaSzukania'
import { czyAktywna } from './nawigacja/ListaNarzedzi'
import IkonaAsystenta from './ikony/IkonaAsystenta'
import UserMenu from './UserMenu'
import Dzwonek from './powiadomienia/Dzwonek'
import ToggleThemeButton from './ToggleThemeButton'

const KLUCZ_KATEGORII = 'vv-kategoria-szukania'
const zapamietanaKategoria = () => { try { return localStorage.getItem(KLUCZ_KATEGORII) } catch { return null } }

/**
 * Nazwa zakładki pokazywana POD ikoną, gdy kursor się na niej zatrzyma.
 *
 * ═══ DLACZEGO POD IKONĄ, A NIE W LINII ═══
 * Pierwsza wersja rozwijała nazwę w linii obok ikony i rozpychała sąsiednie
 * zakładki. Przy przesunięciu kursora po kilku ikonach jednym ruchem pasek
 * falował: ikona pod kursorem się rozwijała, reszta odjeżdżała, a pod kursor
 * wpadała następna (uwaga właściciela 2026-09-30). Podpis położony absolutnie
 * pod ikoną nie zmienia szerokości niczego, więc pasek stoi w miejscu.
 *
 * ═══ OPÓŹNIENIE TYLKO PRZY POKAZYWANIU ═══
 * `group-hover:delay-300`: podpis wysuwa się dopiero po zatrzymaniu kursora,
 * więc przelot nad paskiem nie pokazuje niczego. Chowanie jest natychmiastowe
 * (`delay-0` poza najechaniem), dzięki czemu dwa podpisy nigdy nie wiszą naraz.
 *
 * Klawiatura: `peer-focus-visible`, a nie `focus-within`. Po kliknięciu myszą
 * przycisk zostaje w fokusie także na nowej stronie, więc przy `focus-within`
 * podpis wisiałby tam, dopóki ktoś nie kliknie gdzie indziej.
 *
 * Bez atrybutu `title` na przycisku: przeglądarka pokazałaby wtedy drugi,
 * własny dymek z tą samą treścią. Nazwę dla czytników niesie `aria-label`.
 */
const Podpis = ({ tekst, className = '' }) => (
  <span
    aria-hidden='true'
    className={`pointer-events-none absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2
                whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2.5 py-1.5
                text-xs font-semibold text-gray-800 shadow-md
                opacity-0 -translate-y-1 transition duration-150 delay-0
                group-hover:opacity-100 group-hover:translate-y-0 group-hover:delay-300
                peer-focus-visible:opacity-100 peer-focus-visible:translate-y-0 ${className}`}
  >
    {tekst}
  </span>
)

/**
 * Zakładka paska: SAMA IKONA, nazwa w podpisie pod spodem (patrz `Podpis`).
 * Stałe podpisy zajmowały tyle miejsca, że pasek nie mieścił się na typowym
 * laptopie (decyzja właściciela 2026-09-30).
 */
const NavItem = ({ Icon, label, count, badgeColor, onClick }) => (
  <span className='relative group'>
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className='peer flex items-center px-2.5 py-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50 transition-colors'
    >
      {/* Badge nałożony na ikonę — nie zmienia szerokości przycisku (search nie skacze) */}
      <span className='relative flex-shrink-0'>
        <Icon className='w-5 h-5' />
        {count > 0 && (
          <span className={`absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold text-white rounded-full ${badgeColor}`}>
            {count}
          </span>
        )}
      </span>
    </button>
    <Podpis tekst={label} />
  </span>
)

/**
 * „Sprawdź ofertę” wygląda jak „Asystent doboru” tuż obok: ten sam niebieski
 * przycisk z nazwą (decyzja właściciela 2026-09-30). To osobne narzędzie dla
 * kogoś, kto ma już wycenę, a nie kolejny widok katalogu, więc nie chowa się
 * między szarymi ikonami.
 *
 * Nazwa znika poniżej progu `pasek` (1400 px) dokładnie tak jak przy
 * asystencie: trzy rozwinięte przyciski nie mieszczą się obok wyszukiwarki na
 * laptopie. Wtedy nazwę pokazuje ten sam spokojny podpis co przy ikonach.
 */
const NavOferta = ({ onClick }) => {
  const { t } = useTranslation('wspolne')
  return (
  <span className='relative group'>
    <button
      type='button'
      onClick={onClick}
      aria-label={t('nav.sprawdz_oferte')}
      className='peer flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-semibold
                 text-white bg-blue-600 hover:bg-blue-500 active:bg-blue-700 transition-colors'
    >
      <DocumentCheckIcon className='w-5 h-5 flex-shrink-0' />
      <span className='hidden pasek:inline whitespace-nowrap'>{t('nav.sprawdz_oferte')}</span>
    </button>
    <Podpis tekst={t('nav.sprawdz_oferte')} className='pasek:hidden' />
  </span>
  )
}

/**
 * Główne narzędzie w trybie menu (config/nawigacja: MENU_NARZEDZI). Ten sam niebieski przycisk
 * co dawne „Sprawdź ofertę”, ale jako odnośnik, bo prowadzi prosto na stronę.
 *
 * Nazwa od `xl` (1280 px). Między `lg` a `xl` sama ikona z podpisem pod spodem: „Zostań
 * partnerem”, dwa przyciski z nazwą, „Narzędzia” i konto nie mieszczą się obok wyszukiwarki
 * przy 1024 px. Poniżej `lg` przycisk znika: przy 768 px nawet same ikony wpychały przycisk
 * wyszukiwania pod „Zostań partnerem”, a oba narzędzia są w szufladzie pod przyciskiem menu.
 */
// `nazwaOdPaska`: nazwa dopiero od 1400 px (próg `pasek`), bo trzy przyciski z nazwą nie mieszczą się przy 1280 px.
// `licznik`: plakietka na Rynku (F2), wystaje poza obrys przycisku, liczbę dla czytnika niesie `aria-label`.
const NavNarzedzie = ({ to, takze, Icon, label, sciezka, nazwaOdPaska = false, licznik = 0 }) => {
  const aktywne = czyAktywna({ to, takze }, sciezka)
  return (
    <span className='relative group hidden lg:block'>
      <PlakietkaRynku liczba={licznik} />
      <Link
        to={to}
        aria-label={etykietaZLicznikiem(label, licznik)}
        aria-current={aktywne ? 'page' : undefined}
        className={`peer flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-semibold text-white
                    transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                    focus-visible:ring-offset-2
                    ${aktywne ? 'bg-blue-700' : 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700'}`}
      >
        <Icon aria-hidden='true' className='w-5 h-5 flex-shrink-0' />
        <span className={`hidden whitespace-nowrap ${nazwaOdPaska ? 'pasek:inline' : 'xl:inline'}`}>{label}</span>
      </Link>
      <Podpis tekst={label} className={nazwaOdPaska ? 'pasek:hidden' : 'xl:hidden'} />
    </span>
  )
}

/**
 * Wyróżniona pozycja dla firm („Dodaj siebie"). Jedyna w pasku na kolorze —
 * pozostałe zakładki obsługują przeglądanie, ta ma DOWOZIĆ nowych wykonawców
 * i sklepy, więc świadomie nie wygląda jak reszta. Etykieta widoczna od `md`,
 * nie od `lg` jak w pozostałych: sam plus bez podpisu nie mówi, co się stanie.
 */
const NavCta = ({ Icon, label, onClick }) => {
  const { t } = useTranslation('wspolne')
  return (
  /* `relative` na opakowaniu, nie na przycisku: plakietka ma WYSTAWAĆ poza
     krawędź, a przycisk musi zostać w rytmie paska nawigacji. Gdyby kotwicą był
     sam przycisk, plakietka zmieniłaby jego wysokość i rozjechała wszystkie
     zakładki obok. */
  <span className='relative inline-flex'>
    {PROMOCJA_AKTYWNA && (
      /* Półka wysuwana SPOD przycisku: górne rogi chowa `-mt-2` i niższy `z-0`,
         więc kafelek wygląda, jakby wyjeżdżał zza guzika, a nie leżał obok niego.
         Wystaje kilka pikseli poniżej paska — to celowe, bo tylko dzięki temu
         mieści się w czytelnym rozmiarze przy `h-16` nawigacji. Pasek nie ma
         `overflow-hidden`, więc nic tego nie przycina. */
      <span
        aria-hidden='true'
        className='absolute top-full left-1 -mt-2 z-0 flex items-center gap-1.5
                   pl-2.5 pr-3 pt-2.5 pb-1 rounded-b-xl whitespace-nowrap pointer-events-none
                   bg-blue-50 border border-t-0 border-blue-100 shadow-sm
                   text-[11px] leading-none font-semibold text-blue-600'
      >
        <ClockIcon className='w-3.5 h-3.5 flex-shrink-0' />
        {t('nav.plakietka_cta', { defaultValue: PLAKIETKA_CTA })}
      </span>
    )}
    <button
      type='button'
      onClick={onClick}
      className='relative z-10 flex items-center gap-2 pl-3 pr-2 py-2 rounded-xl text-sm font-semibold text-white
                 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 transition-colors
                 shadow-sm shadow-blue-600/25'
    >
      <Icon className='w-5 h-5 flex-shrink-0' />
      <span className='whitespace-nowrap'>{label}</span>
      {/* Strzałka zapowiada przejście na inną stronę — bez niej przycisk w pasku
          czyta się jak przełącznik widoku, a nie wejście w osobną ścieżkę. */}
      <ChevronRightIcon className='w-4 h-4 flex-shrink-0 text-white/80' />
      {/* Treść plakietki powtórzona dla czytnika ekranu — sama plakietka jest
          `aria-hidden`, bo wizualnie należy do przycisku, a nie stoi obok niego. */}
      {PROMOCJA_AKTYWNA && <span className='sr-only'>, {t('nav.plakietka_cta', { defaultValue: PLAKIETKA_CTA })}</span>}
    </button>
  </span>
  )
}

const Navbar = () => {
  const { t } = useTranslation('wspolne')
  // Konto firmowe (2026-10-11): logo prowadzi na pulpit, a „Sprawdź ofertę” znika z paska (firma nie sprawdza
  // cudzych ofert; narzędzie zostaje w menu „Narzędzia”).
  const { firmowe } = useAuth()
  const startowa = firmowe && jestPolska ? '/pulpit' : '/'
  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'
  // Które narzędzia stoją w górnym pasku (flagi w config/nawigacja):
  // 'menu'  – dwa główne z nazwą i menu „Narzędzia”, na każdej stronie (decyzja właściciela 2026-10-08),
  // 'brak'  – sam pasek boczny: narzędzia mieszkają w nim, na stronie głównej zostają ikony,
  // 'ikony' – dawny układ.
  const tryb = MENU_NARZEDZI ? 'menu' : (PASEK_NARZEDZI && !isHomePage ? 'brak' : 'ikony')
  // Przycisk menu z szufladą poniżej `lg` w obu nowych układach.
  const zSzuflada = MENU_NARZEDZI || PASEK_NARZEDZI
  const { comparisonCount } = useSelection()
  const licznikRynku = useLicznikRynku(jestPolska && RYNEK_WLACZONY)
  const nazwaRynku = firmowe ? t('nav.rynek_ofert') : t('nav.oglos_na_rynku')

  const [navSearch, setNavSearch] = useState('')
  // Kategoria wyszukiwarki (2026-10-12): na listingu ta, na którym stoimy, gdzie indziej ostatnio wybrana, na start moduły.
  const [kategoria, setKategoria] = useState(() => kategoriaZeSciezki(location.pathname)?.id || zapamietanaKategoria() || 'panele')
  useEffect(() => {
    const k = kategoriaZeSciezki(location.pathname)
    if (k) setKategoria(k.id)
  }, [location.pathname])
  const wybierzKategorie = (id) => {
    setKategoria(id)
    try { localStorage.setItem(KLUCZ_KATEGORII, id) } catch { /* bez pamięci przeglądarki wybór żyje do odświeżenia */ }
  }

  // Szuflada z narzędziami (telefon i tablet, przy włączonym pasku narzędzi).
  // Zamyka się przy każdej zmianie strony, także tej z wyszukiwarki obok.
  const [szuflada, setSzuflada] = useState(false)
  const przyciskMenu = useRef(null)
  const zamknijSzuflade = useCallback(() => setSzuflada(false), [])
  useEffect(() => { setSzuflada(false) }, [location.pathname])

  // Sync search input with URL param when navigating between product pages
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setNavSearch(params.get('search') || '')
  }, [location.pathname])

  const handleSearch = () => {
    const query = navSearch.trim()
    const cel = dostepneKategorie().find((k) => k.id === kategoria) || dostepneKategorie()[0]
    // Na listingu wybranej kategorii zostają jego filtry; inna kategoria to przejście na jej listing.
    const isOnProductPage = location.pathname === cel.trasa
    const params = new URLSearchParams(location.search)

    if (query) {
      params.set('search', query)
    } else {
      params.delete('search')
    }
    params.set('page', '1')

    if (isOnProductPage) {
      navigate({ search: params.toString() })
    } else {
      navigate(`${cel.trasa}${query ? `?search=${encodeURIComponent(query)}&page=1` : ''}`)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch()
  }

  const wyszukiwarka = !isHomePage && (
    /* `min-w-0` na opakowaniu I na polu: bez tego pole trzyma własną
       szerokość minimalną (~250 px z atrybutu `size`), więc na telefonie
       pasek nie mieścił się w ekranie i cała strona przewijała się w bok
       o kilkadziesiąt pikseli. Z `min-w-0` pole kurczy się jako ostatnie,
       a na szerokich ekranach nic się nie zmienia (jest tam miejsce). */
    <div className='flex items-center gap-2 flex-1 min-w-0 max-w-xl'>
      <div className='flex flex-1 min-w-0 items-stretch rounded-lg border border-gray-200 bg-gray-50 transition-colors
                      focus-within:border-transparent focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500'>
        {/* Wybór kategorii od `sm`: na telefonie pasek dzieli miejsce z „Ogłoś na Rynku” i kontem (2026-10-23),
            a bez wyboru szukamy w kategorii z adresu albo ostatnio wybranej. */}
        <span className='hidden sm:flex'>
          <KategoriaSzukania wybrana={kategoria} onWybierz={wybierzKategorie} />
        </span>
        <input
          type='text'
          value={navSearch}
          onChange={(e) => setNavSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('nav.szukaj_produktu')}
          className='flex-1 min-w-0 rounded-lg sm:rounded-l-none bg-transparent pl-3 pr-3 py-2 text-sm focus:outline-none'
        />
      </div>
      <button
        onClick={handleSearch}
        className='p-2 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white rounded-lg transition-colors flex-shrink-0'
        aria-label={t('nav.szukaj')}
      >
        <SearchIcon fontSize='small' />
      </button>
    </div>
  )

  // Wersja krajowa: bez programu partnerskiego (polskie firmy i umowy) i bez
  // „Sprawdź ofertę” (reguły pod polskie dotacje i VAT), dopóki nie mają
  // odpowiedników w danym kraju. Na pulpicie firmy przycisk schowany (uwaga właściciela 2026-10-12): firma
  // już jest partnerem, a pasek zostaje dla narzędzi.
  const naPulpicie = location.pathname === '/pulpit' || location.pathname.startsWith('/pulpit/')
  // „Dla firm PV” (2026-10-22, dawniej „Zostań partnerem” z rozdrożem `/dodaj-siebie`): jedna droga dla firm, strona
  // `/dla-firm` i konto firmowe. Konto firmowe ma już w pasku „Rynek ofert”, więc przycisku nie widzi.
  const ctaDlaFirm = jestPolska && !naPulpicie && !firmowe && <NavCta Icon={PlusCircleIcon} label={t('nav.dla_firm_pv')} onClick={() => navigate('/dla-firm')} />
  // Na polskiej stronie głównej (od 2026-10-28) wejście dla firm to zakładka „Sprzedaję instalacje PV”, więc przycisku
  // w pasku tam nie ma (uwaga właściciela). W szufladzie na telefonie zostaje.
  const ctaDlaFirmWPasku = !(isHomePage && jestPolska) && ctaDlaFirm
  // Konto firmowe (2026-10-30): logo prowadzi na pulpit, więc na stronę główną voltvalue.pl nie było jak wrócić.
  const doStronyGlownej = firmowe && jestPolska && !isHomePage

  return (
    <nav className='h-16 flex items-center justify-between sticky top-0 z-20 bg-white shadow-sm border-b border-gray-100 px-4 md:px-8 gap-2 sm:gap-4'>
      {/* Logo na każdej stronie, także głównej (2026-10-28): nowa strona główna nie ma logo w treści,
          a bez niego pasek nie mówił, gdzie jesteśmy. */}
      {zSzuflada ? (
        /* Przycisk menu przed logo, tylko poniżej `lg`: wyżej narzędzia są w menu
           „Narzędzia” (albo w pasku po lewej); to jedyna droga do narzędzi na telefonie. */
        <div className='flex items-center gap-2 flex-shrink-0'>
          <button
            ref={przyciskMenu}
            type='button'
            onClick={() => setSzuflada(true)}
            aria-label={t('nav.menu')}
            aria-haspopup='dialog'
            aria-expanded={szuflada}
            aria-controls='szuflada-narzedzi'
            className='lg:hidden flex items-center justify-center p-2 -ml-2 rounded-lg text-gray-600
                       hover:text-blue-600 hover:bg-gray-50 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
          >
            <Bars3Icon aria-hidden='true' className='w-6 h-6' />
          </button>
          <Link to={startowa} className='flex-shrink-0'>
            <img
              src={`${process.env.PUBLIC_URL}/navLogo.png`}
              alt='navLogo'
              className='w-20 sm:w-24 h-auto hover:scale-105 transition duration-200 ease-in-out'
            />
          </Link>
          <SzufladaNarzedzi
            id='szuflada-narzedzi'
            otwarta={szuflada}
            onZamknij={zamknijSzuflade}
            przyciskRef={przyciskMenu}
            dlaFirm={Boolean(ctaDlaFirm)}
            stronaGlowna={doStronyGlownej}
          />
        </div>
      ) : (
        <Link to={startowa} className='flex-shrink-0'>
          <img
            src={`${process.env.PUBLIC_URL}/navLogo.png`}
            alt='navLogo'
            className='w-24 h-auto hover:scale-105 transition duration-200 ease-in-out'
          />
        </Link>
      )}

      {tryb === 'menu' ? (
        /* „Zostań partnerem” zaraz za lupą, po lewej (decyzja właściciela 2026-10-08): po prawej
           stoją narzędzia, a wejście dla firm to nie narzędzie. Na stronie głównej, bez
           wyszukiwarki, przycisk stoi po prostu na początku paska. */
        <div className='flex items-center gap-3 flex-1 min-w-0'>
          {wyszukiwarka}
          {ctaDlaFirmWPasku && <span className='hidden md:block flex-shrink-0'>{ctaDlaFirmWPasku}</span>}
        </div>
      ) : wyszukiwarka}

      <div className='flex items-center gap-1 flex-shrink-0'>
      <div className='hidden md:flex items-center gap-1'>
        {/* CTA dla firm zawsze PIERWSZE w grupie zakladek — takze na stronie
            glownej, gdzie wczesniej stalo posrodku paska. Srodek zwracal na siebie
            uwage, ale wyrywal przycisk z listy nawigacji: wygladal na element
            osobny, a nie na pierwsza pozycje menu. */}
        {tryb !== 'menu' && ctaDlaFirmWPasku}
        {/* Tryb menu: dwa narzędzia, po które ludzie przychodzą, stoją z nazwą, reszta (także
            Kompatybilność i Baza wiedzy) jest w menu „Narzędzia”. Asystent to tu zwykły odnośnik:
            pozostałe kreatory z dawnej rozwijanej listy są w menu, w grupie „Dobór”.
            Samo menu od `lg`; niżej te same grupy otwiera przycisk przy logo (szuflada). */}
        {tryb === 'menu' && (
          <>
            {doStronyGlownej && (
              <NavNarzedzie to='/' Icon={HomeIcon} label={t('nav.strona_glowna')} sciezka={location.pathname} />
            )}
            {jestPolska && !firmowe && (
              <NavNarzedzie to='/sprawdz-oferte' Icon={DocumentCheckIcon} label={t('nav.sprawdz_oferte')} sciezka={location.pathname} />
            )}
            {/* Rynek zleceń (decyzja właściciela 2026-10-21): bez konta i dla konta prywatnego „Ogłoś na Rynku”,
                dla konta firmowego „Rynek ofert” z ogłoszeniami pokazanymi jego firmie (ten sam adres, inny widok). */}
            {jestPolska && RYNEK_WLACZONY && (
              <NavNarzedzie to='/rynek' Icon={MegaphoneIcon} sciezka={location.pathname} nazwaOdPaska={!firmowe}
                            label={nazwaRynku} licznik={licznikRynku} />
            )}
            <NavNarzedzie
              to='/dobor-zestawu-pv' takze={['/dobierz-zestaw']}
              Icon={IkonaAsystenta} label={t('menu.asystent_doboru')} sciezka={location.pathname}
            />
            <span className='hidden lg:block'>
              <MenuNarzedzi />
            </span>
          </>
        )}
        {/* Dawny układ z ikonami. W trybie 'brak' narzędzia są w pasku po lewej, a tu zostaje
            tylko to, co nie jest narzędziem: wejście dla firm i porównanie. */}
        {tryb === 'ikony' && (
          <>
            <MenuAsystentow />
            {jestPolska && !firmowe && <NavOferta onClick={() => navigate('/sprawdz-oferte')} />}
            <NavItem Icon={PuzzlePieceIcon} label={t('nav.kompatybilnosc')} onClick={() => navigate('/kompatybilnosc')} />
          </>
        )}
        {/* Przy menu „Narzędzia” porównywarka jest w nim (2026-10-11), bez ikony wagi w pasku; dawny układ bez menu
            zostawia ikonę, bo nie ma innego wejścia. */}
        {tryb === 'ikony' && (
          <NavItem
            Icon={ScaleIcon}
            label={t('nav.porownanie')}
            count={comparisonCount}
            badgeColor='bg-blue-600'
            onClick={() => navigate('/porownanie')}
          />
        )}
        {/* „Obserwowane" przeniesione do panelu konta (/konto → Obserwowane) —
            pasek jest wspólny dla wszystkich, a to widok jednej osoby. */}
        {/* Artykuły bazy wiedzy są tylko po polsku. */}
        {tryb === 'ikony' && jestPolska && (
          <NavItem Icon={BookOpenIcon} label={t('nav.baza_wiedzy')} onClick={() => navigate('/baza-wiedzy')} />
        )}

      </div>

        {/* Rynek poniżej `lg` (2026-10-23, makieta „VoltValue dla domu”, D1): wyżej stoi w grupie narzędzi, a dotąd
            na telefonie i tablecie nie było go wcale. Poza stroną główną sama ikona z nazwą dla czytnika, bo pasek
            dzieli miejsce z wyszukiwarką (przy 820 px z nazwą zostawały z pola dwie litery); na stronie głównej
            wyszukiwarki w pasku nie ma, więc mieści się nazwa. */}
        {jestPolska && RYNEK_WLACZONY && (
          <span className='relative lg:hidden flex flex-shrink-0'>
            <PlakietkaRynku liczba={licznikRynku} />
            <Link
              to='/rynek'
              aria-label={etykietaZLicznikiem(nazwaRynku, licznikRynku)}
              className={`flex flex-shrink-0 items-center gap-1.5 px-2 ${isHomePage ? 'sm:px-3' : ''} py-2 rounded-lg text-sm font-semibold
                         text-white bg-blue-600 hover:bg-blue-500 active:bg-blue-700 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
            >
              <MegaphoneIcon aria-hidden='true' className='w-5 h-5 flex-shrink-0' />
              <span className={`${isHomePage ? '' : 'hidden'} whitespace-nowrap`}>{nazwaRynku}</span>
            </Link>
          </span>
        )}

        {/* Motyw i konto po kresce — to USTAWIENIA, nie kolejne zakładki.
            Konto widać na każdej szerokości (2026-10-23): bez logowania w pasku telefonu nie dało się dojść do
            „Moich ogłoszeń”. Przełącznik motywu na telefonie jest w szufladzie menu, żeby zmieściła się wyszukiwarka. */}
        <span className='flex items-center gap-1 md:ml-1 md:pl-2 md:border-l md:border-gray-200'>
          <span className={zSzuflada ? 'hidden sm:inline-flex' : 'inline-flex'}>
            <ToggleThemeButton />
          </span>
          {/* Konta są jeszcze tylko po polsku: w wersji krajowej bez logowania. */}
          {jestPolska && (
            <span className='hidden md:inline-flex'>
              <Dzwonek />
            </span>
          )}
          {jestPolska && <UserMenu />}
        </span>
      </div>
    </nav>
  )
}

export default Navbar
